import { feverEmoji } from '../utils/format.js';

const STATUS_LABELS = {
  active:    'active',
  queued:    '⏳ queued',
  paused:    'paused',
  backlog:   'backlog',
  done:      'done',
  cancelled: 'cancelled',
};

// Ветка 3 (план 2026-05-13-wbs-3level-and-pipeline-ux.md): tooltip на статусе
// помогает отличить queued «черновик до фиксации baseline» от paused
// «приостановленный с уже зафиксированным baseline».
const STATUS_HINTS = {
  queued: 'Черновик — план в проработке, baseline ещё не зафиксирован',
  paused: 'Приостановлен — baseline зафиксирован, работа временно остановлена',
};

/**
 * Project multi-select с группировкой по портфелю и live-search.
 *
 * Phase 2 Wave 2: при наличии портфелей (`portfolios` непустой массив) проекты
 * группируются по `portfolio_id` с заголовками-разделителями и кнопкой
 * «выбрать все в группе». Поиск-инпут вверху панели фильтрует список по name
 * (case-insensitive substring).
 *
 * @param {{
 *   items: Array<{id:number, name:string, status:string, fever_zone?:string, portfolio_id?:number}>,
 *   selectedIds: Iterable<number>,
 *   onChange: (Set<number>) => void,
 *   portfolios?: Array<{id:number, code:string, name:string, sort_order:number}>,
 * }} cfg
 */
export function createProjectMultiSelect({ items, selectedIds, onChange, portfolios = [] }) {
  const root = document.createElement('div');
  root.className = 'gantt-multiselect';

  const toggleBtn = document.createElement('button');
  toggleBtn.type = 'button';
  toggleBtn.className = 'ms-toggle';

  const panel = document.createElement('div');
  panel.className = 'ms-panel';
  panel.hidden = true;

  let selected = new Set(selectedIds);
  let searchQuery = '';

  function activeQueuedPausedIds() {
    return items
      .filter(p => ['active', 'queued', 'paused'].includes(p.status))
      .map(p => p.id);
  }

  function summaryLabel() {
    if (selected.size === 0)              return 'Ничего не выбрано';
    if (selected.size === items.length)   return 'Все проекты';
    const aqp = activeQueuedPausedIds();
    if (selected.size === aqp.length && aqp.every(id => selected.has(id))) {
      return 'Все активные';
    }
    return `Выбрано: ${selected.size}`;
  }

  function refreshToggle() {
    toggleBtn.textContent = `${summaryLabel()} ▾`;
  }

  function renderList() {
    const q = searchQuery.trim().toLowerCase();
    const visible = q
      ? items.filter(p => p.name.toLowerCase().includes(q))
      : items;

    if (portfolios.length === 0) {
      // Без группировки — просто список.
      list.innerHTML = visible.map(renderItemRow).join('');
      return;
    }

    // Группируем по portfolio_id; проекты без portfolio_id → специальный bucket.
    const buckets = new Map(); // pid -> Array<item>
    const NO_PORTFOLIO = 0;
    for (const p of visible) {
      const pid = p.portfolio_id ?? NO_PORTFOLIO;
      if (!buckets.has(pid)) buckets.set(pid, []);
      buckets.get(pid).push(p);
    }

    const sortedPortfolios = [...portfolios].sort((a, b) => a.sort_order - b.sort_order);

    const html = [];
    for (const pf of sortedPortfolios) {
      const bucket = buckets.get(pf.id);
      if (!bucket || !bucket.length) continue;
      html.push(renderGroupHeader(pf, bucket));
      html.push(bucket.map(renderItemRow).join(''));
    }
    if (buckets.has(NO_PORTFOLIO)) {
      const bucket = buckets.get(NO_PORTFOLIO);
      html.push(renderGroupHeader({ id: NO_PORTFOLIO, code: '—', name: 'Без портфеля' }, bucket));
      html.push(bucket.map(renderItemRow).join(''));
    }
    list.innerHTML = html.join('');
  }

  function renderGroupHeader(pf, bucket) {
    const allSelected = bucket.every(p => selected.has(p.id));
    return `
      <div class="ms-group-header">
        <span class="ms-group-name">${escapeHtml(pf.name)}</span>
        <button type="button" class="ms-group-btn"
                data-group-toggle="${pf.id}"
                data-state="${allSelected ? 'all' : 'partial'}"
                title="Выбрать/снять группу">
          ${allSelected ? 'Снять группу' : 'Выбрать группу'}
        </button>
      </div>`;
  }

  function renderItemRow(p) {
    const statusLabel = STATUS_LABELS[p.status] ?? p.status;
    const statusHint  = STATUS_HINTS[p.status];
    const statusAttr  = statusHint ? ` title="${escapeAttr(statusHint)}"` : '';
    return `
      <label class="ms-item ms-item-status-${p.status}">
        <input type="checkbox" data-pid="${p.id}"${selected.has(p.id) ? ' checked' : ''}>
        <span class="ms-item-fever">${feverEmoji(p.fever_zone)}</span>
        <span class="ms-item-name" title="${escapeAttr(p.name)}">${escapeHtml(p.name)}</span>
        <span class="ms-item-status"${statusAttr}>${statusLabel}</span>
      </label>`;
  }

  function commitChange() {
    refreshToggle();
    renderList(); // обновить group-header «Выбрать/Снять группу»
    onChange(new Set(selected));
  }

  // Search bar
  const searchBar = document.createElement('input');
  searchBar.type = 'search';
  searchBar.placeholder = 'Поиск по имени…';
  searchBar.className = 'ms-search';
  searchBar.addEventListener('input', (e) => {
    searchQuery = e.target.value;
    renderList();
  });
  panel.appendChild(searchBar);

  // Presets row.
  const presets = document.createElement('div');
  presets.className = 'ms-presets';
  presets.innerHTML = `
    <button type="button" data-preset="all">Все</button>
    <button type="button" data-preset="active">Активные</button>
    <button type="button" data-preset="reset">Сбросить</button>
    <button type="button" data-preset="done" class="ms-presets-done">Готово</button>
  `;
  presets.addEventListener('click', e => {
    const btn = e.target.closest('button[data-preset]');
    if (!btn) return;
    const kind = btn.dataset.preset;
    if (kind === 'all')    selected = new Set(items.map(p => p.id));
    if (kind === 'active') selected = new Set(activeQueuedPausedIds());
    if (kind === 'reset')  selected = new Set();
    if (kind !== 'done') commitChange();
    panel.hidden = true;
  });
  panel.appendChild(presets);

  // Items list (rendered dynamically by renderList).
  const list = document.createElement('div');
  list.className = 'ms-list';
  panel.appendChild(list);

  // Click delegation для item-checkbox + group-toggle.
  list.addEventListener('change', (e) => {
    const cb = e.target.closest('input[type=checkbox][data-pid]');
    if (!cb) return;
    const pid = Number(cb.dataset.pid);
    if (cb.checked) selected.add(pid);
    else            selected.delete(pid);
    commitChange();
  });
  list.addEventListener('click', (e) => {
    const btn = e.target.closest('button[data-group-toggle]');
    if (!btn) return;
    const pfId = Number(btn.dataset.groupToggle);
    const bucketItems = items.filter(p => (p.portfolio_id ?? 0) === pfId);
    const allSelected = bucketItems.every(p => selected.has(p.id));
    if (allSelected) bucketItems.forEach(p => selected.delete(p.id));
    else             bucketItems.forEach(p => selected.add(p.id));
    commitChange();
  });

  // Toggle open/close.
  toggleBtn.addEventListener('pointerdown', e => {
    e.stopPropagation();
    panel.hidden = !panel.hidden;
    if (!panel.hidden) {
      // Перерендерить список — состояние selected могло поменяться извне.
      renderList();
      setTimeout(() => searchBar.focus(), 0);
    }
  });
  document.addEventListener('pointerdown', e => {
    if (panel.hidden) return;
    if (root.contains(e.target)) return;
    panel.hidden = true;
  }, true);
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && !panel.hidden) panel.hidden = true;
  });

  root.appendChild(toggleBtn);
  root.appendChild(panel);
  refreshToggle();
  renderList();
  return root;
}

function escapeHtml(s) {
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}
function escapeAttr(s) {
  return escapeHtml(s).replaceAll('"', '&quot;');
}
